import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { useTheme } from '../contexts/ThemeContext';
import PredictionModal from '../components/PredictionModal';
import PredictionBubble from '../components/PredictionBubble';
import { getPredictionsDataPath } from '../utils/dataParser';
import { 
  CACHE_VALIDITY_HOURS, 
  PREDICTIONS_CACHE_KEY, 
  PREDICTIONS_CACHE_TIMESTAMP_KEY,
  TRACKING_EVENTS
} from '../constants';
import { getCachedPredictions, cachePredictions } from '../utils/localStorage';

const HomeContainer = styled.div`
  padding-top: 100px;
  min-height: 100vh;
  
  @media (max-width: 768px) {
    padding-top: 80px;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 2rem;
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 3rem;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const MainSection = styled.div`
  
`;

const SidebarSection = styled.div`
  
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 1.5rem 0;
  color: ${props => props.theme.colors.text};
`;

const CurrentYearBadge = styled.div`
  display: inline-block;
  background: ${props => props.theme.colors.activeGradient};
  color: ${props => props.theme.colors.text};
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
`;

const PredictionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const InfoBox = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
`;

const InfoTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
  color: ${props => props.theme.colors.text};
`;

const InfoText = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.textSecondary};
  margin: 0 0 1rem 0;
`;

const TimelineLink = styled(Link)`
  display: inline-block;
  padding: 1rem 2rem;
  background: ${props => props.theme.colors.activeGradient};
  color: ${props => props.theme.colors.text};
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  text-align: center;
  
  &:hover {
    background: ${props => props.theme.colors.activeGradientHover};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${props => props.theme.colors.shadow};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 40vh;
  color: ${props => props.theme.colors.textSecondary};
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${props => props.theme.colors.borderLight};
  border-top: 3px solid ${props => props.theme.colors.text};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

function HomePage() {
  const { themeData } = useTheme();
  const posthog = usePostHog();
  const [predictions, setPredictions] = useState([]);
  const [selectedPrediction, setSelectedPrediction] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const currentYear = new Date().getFullYear();

  // Simple CSV line parser
  const parseCSVLine = useCallback((line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current);
    return result;
  }, []);

  // CSV parser with multi-line support
  const parseCSV = useCallback((csvText) => {
    const lines = csvText.split('\n');
    const result = [];
    let currentRow = '';
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      if (currentRow) {
        currentRow += '\n' + line;
      } else {
        currentRow = line;
      }
      
      let quoteCount = 0;
      for (let j = 0; j < currentRow.length; j++) {
        if (currentRow[j] === '"') quoteCount++;
      }
      
      if (quoteCount % 2 === 0) {
        result.push(parseCSVLine(currentRow));
        currentRow = '';
      }
    }
    
    if (currentRow) {
      result.push(parseCSVLine(currentRow));
    }
    
    return result;
  }, [parseCSVLine]);

  // Load data from CSV
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        
        // Use public dataset cache (HomePage always uses public data)
        const cacheKey = `${PREDICTIONS_CACHE_KEY}_public`;
        const timestampKey = `${PREDICTIONS_CACHE_TIMESTAMP_KEY}_public`;
        
        const cachedData = getCachedPredictions(
          cacheKey,
          timestampKey,
          CACHE_VALIDITY_HOURS
        );
        
        if (cachedData) {
          setPredictions(cachedData);
          setIsLoading(false);
          return;
        }
        
        // Always use public dataset for HomePage
        const dataPath = getPredictionsDataPath(false);
        const response = await fetch(dataPath);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const csvText = await response.text();
        
        const allRows = parseCSV(csvText);
        if (allRows.length === 0) {
          throw new Error('Empty CSV file');
        }
        
        const headers = allRows[0].map(h => h.trim());
        const parsedData = [];
        
        for (let i = 1; i < allRows.length; i++) {
          const values = allRows[i];
          if (values && values.length > 0) {
            const row = {};
            headers.forEach((header, index) => {
              row[header] = values[index] ? values[index].trim().replace(/^"|"$/g, '') : '';
            });
            
            if (row.id && row.display_year && row.title) {
              parsedData.push({
                id: parseInt(row.id, 10),
                display_year: parseInt(row.display_year, 10),
                timeframe: row.timeframe || '',
                title: row.title,
                text: row.rewritten || row.text || '',
                tags: row.tags || '',
                image: row.image || '/images/placeholder.svg',
                source: row.source || '',
                author: row.author || '',
                date: row.date || '',
                link: row.link || '',
                realized: row.realized || '',
                realized_date: row.realized_date || '',
                realized_links: row.realized_links || '',
                raw_text: row.raw_text || ''
              });
            }
          }
        }
        
        cachePredictions(
          cacheKey,
          timestampKey,
          parsedData
        );
        
        setPredictions(parsedData);
        posthog?.capture(TRACKING_EVENTS.PREDICTIONS_LOADED_SERVER, { 
          prediction_count: parsedData.length 
        });
      } catch (error) {
        console.error('Error loading CSV data:', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [parseCSV, posthog]);

  // Filter predictions for current year
  const currentYearPredictions = useMemo(() => {
    return predictions.filter(p => p.display_year === currentYear);
  }, [predictions, currentYear]);

  const handlePredictionClick = (prediction) => {
    setSelectedPrediction(prediction);
    posthog?.capture(TRACKING_EVENTS.PREDICTION_CLICKED, { 
      prediction_id: prediction.id,
      prediction_year: prediction.display_year,
      prediction_title: prediction.title,
      prediction_author: prediction.author 
    });
  };

  const handleCloseModal = () => {
    setSelectedPrediction(null);
    posthog?.capture(TRACKING_EVENTS.PREDICTION_MODAL_CLOSED);
  };

  if (isLoading) {
    return (
      <HomeContainer theme={themeData}>
        <LoadingContainer theme={themeData}>
          <LoadingSpinner theme={themeData} />
          <p>Loading predictions...</p>
        </LoadingContainer>
      </HomeContainer>
    );
  }

  if (error) {
    return (
      <HomeContainer theme={themeData}>
        <LoadingContainer theme={themeData}>
          <p style={{ color: '#ff4444' }}>Error: {error}</p>
        </LoadingContainer>
      </HomeContainer>
    );
  }

  return (
    <HomeContainer theme={themeData}>
      <ContentWrapper>
        <MainSection>
          <SectionTitle theme={themeData}>Predictions for {currentYear}</SectionTitle>
          <CurrentYearBadge theme={themeData}>Current Year</CurrentYearBadge>
          
          {currentYearPredictions.length > 0 ? (
            <PredictionsGrid>
              {currentYearPredictions.map((prediction, index) => (
                <PredictionBubble
                  key={prediction.id}
                  prediction={prediction}
                  onClick={() => handlePredictionClick(prediction)}
                  delay={index * 0.1}
                />
              ))}
            </PredictionsGrid>
          ) : (
            <InfoText theme={themeData}>
              No predictions for {currentYear} in our database.
            </InfoText>
          )}
          
          <TimelineLink to="/timeline" theme={themeData}>
            View Complete Timeline →
          </TimelineLink>
        </MainSection>
        
        <SidebarSection>
          <InfoBox theme={themeData}>
            <InfoTitle theme={themeData}>About This Project</InfoTitle>
            <InfoText theme={themeData}>
              The Singularity Countdown tracks expert predictions about technological advancement
              and the approach to artificial general intelligence.
            </InfoText>
            <InfoText theme={themeData}>
              We aggregate forecasts from leading futurists and technology experts to monitor
              how close we are to reaching the technological singularity.
            </InfoText>
          </InfoBox>
          
          <InfoBox theme={themeData}>
            <InfoTitle theme={themeData}>What You'll Find</InfoTitle>
            <InfoText theme={themeData}>
              • <strong>Expert Predictions</strong> from 15+ futurists
            </InfoText>
            <InfoText theme={themeData}>
              • <strong>Timeline</strong> spanning 2025 to 2100
            </InfoText>
            <InfoText theme={themeData}>
              • <strong>Accuracy Tracking</strong> of past predictions
            </InfoText>
            <InfoText theme={themeData}>
              • <strong>Multiple Perspectives</strong> from proponents and skeptics
            </InfoText>
          </InfoBox>
        </SidebarSection>
      </ContentWrapper>

      {selectedPrediction && (
        <PredictionModal 
          prediction={selectedPrediction}
          onClose={handleCloseModal}
          showDebugInfo={false}
        />
      )}
    </HomeContainer>
  );
}

export default HomePage;
